/**
 * Contenu éditorial de la page /mariage (page de niveau 2 sous Événements).
 * Texte fourni par le client — à utiliser tel quel côté FR.
 */

export const MARIAGE_CONTENT = {
  fr: {
    meta: {
      title: "Mariage à Ville-d'Avray, près de Versailles",
      description:
        "Votre mariage dans un hôtel particulier à Ville-d'Avray (92) : terrasse de 240 m² sur parc, de 12 à 100 invités assis, menus de mariage de 99 à 125 € par invité.",
      keywords:
        "mariage Ville d'Avray, lieu de mariage près de Versailles, salle mariage 92, réception mariage Hauts-de-Seine, menu mariage",
    },
    breadcrumb: [
      { label: "Accueil", to: "/" },
      { label: "Événements", to: "/evenements" },
      { label: "Mariage" },
    ],
    overline: "Mariage · Ville-d'Avray (92)",
    h1: "Votre mariage dans un hôtel particulier à Ville-d'Avray",
    intro:
      "Un hôtel particulier du XIXe siècle, une terrasse de 240 m² surplombant un parc arboré, deux salons décorés avec soin et une cuisine entièrement faite maison : L'Entracte accueille votre mariage de 12 à 100 invités assis, entre Saint-Cloud et Versailles, à 15 minutes de Paris.",
    ctaDevis: "Demander un devis mariage",
    ctaCall: "Appeler le 01 82 00 18 67",
    heroAlt: "Mariage sur la terrasse de L'Entracte, hôtel particulier à Ville-d'Avray",

    brief: {
      title: "Votre mariage à L'Entracte en bref",
      caption: "Informations clés pour un mariage à L'Entracte",
      rows: [
        ["Lieu", "Hôtel particulier, 3 rue de Versailles, 92410 Ville-d'Avray"],
        ["Capacité", "De 12 à 100 invités assis, jusqu'à 130 en cocktail"],
        ["Espaces", "Grand Salon, Petit Salon et terrasse de 240 m² sur parc"],
        ["Formats", "Vin d'honneur, cocktail, déjeuner ou dîner assis, buffet"],
        ["Menu de mariage", "De 99 à 125 € par invité, vin, eaux et café compris"],
        ["Menu enfant", "40 € (plat et dessert)"],
        [
          "Horaires",
          "Déjeuner 12h30-15h30 ou dîner 19h30-22h30, prolongeable jusqu'à minuit (400 € par heure supplémentaire)",
        ],
        [
          "Accès",
          "Gare de Sèvres – Ville-d'Avray (lignes L et U) à 1 km · stationnement à proximité immédiate",
        ],
        ["Accessibilité", "Espaces accessibles aux personnes à mobilité réduite"],
        ["Location de salle seule", "Non, la restauration est assurée par notre cuisine"],
      ],
    },

    human: {
      title: "Un mariage à taille humaine",
      p1: "Entre les grands domaines de réception de l'Ouest parisien et les salles des fêtes, L'Entracte propose une autre voie : un mariage intime et élégant, dans une maison chargée d'histoire. Vous profitez d'un lieu privatisé, d'une vraie cuisine de restaurant plutôt que d'un traiteur, et d'une équipe qui connaît chaque recoin de la maison.",
      p2: "Michel et son équipe vous accompagnent de la première visite jusqu'au dernier café.",
      alt: "Grand Salon de L'Entracte dressé pour un repas de mariage",
    },

    why: {
      title: "Pourquoi choisir L'Entracte pour votre mariage ?",
      items: [
        "Un cadre d'exception à taille humaine : un hôtel particulier et un parc arboré, sans l'effet « usine à mariages ».",
        "Une cuisine 100 % faite maison : chaque plat est préparé sur place par notre chef, à partir de produits frais et de saison.",
        "Un plan B intégré : si la météo se gâte, le cocktail et le repas se tiennent dans nos salons.",
        "Tout est compris : privatisation, menu du chef, vin, eaux, café, service et mise en place, avec un seul interlocuteur.",
        "Facile d'accès pour vos invités : en train depuis Paris Saint-Lazare et La Défense, ou en voiture à 15 minutes de Paris.",
      ],
    },

    spaces: {
      title: "Les espaces de votre mariage",
      items: [
        {
          name: "La terrasse de 240 m²",
          body:
            "Jusqu'à 90 invités assis et 120 en cocktail. Nichée dans un écrin de verdure et surplombant un parc arboré, elle accueille le vin d'honneur et, aux beaux jours, le repas en plein air.",
          alt: "Terrasse de 240 m² de L'Entracte pour un vin d'honneur de mariage",
        },
        {
          name: "Le Grand Salon",
          body:
            "De 30 à 65 invités assis, jusqu'à 75 en cocktail. Lumineux et décoré avec soin, il accueille un repas assis élégant en toute saison.",
          alt: "Grand Salon de L'Entracte dressé pour un repas de mariage",
        },
        {
          name: "Le Petit Salon",
          body: "De 10 à 30 invités, pour un mariage en tout petit comité ou un repas de famille le lendemain.",
          alt: "Petit Salon de L'Entracte pour un mariage en petit comité",
        },
        {
          name: "La privatisation totale",
          body: "De 50 à 100 invités assis, jusqu'à 130 en cocktail : toute la maison vous est réservée.",
          alt: "Hôtel particulier de L'Entracte entièrement privatisé pour un mariage",
        },
      ],
      table: {
        caption: "Espace privatisé selon le nombre d'invités adultes",
        head: ["Nombre d'invités adultes", "Espace privatisé"],
        rows: [
          ["De 12 à 29", "Le Petit Salon"],
          ["De 30 à 49", "Le Grand Salon"],
          ["50 et plus (avec cocktail apéritif)", "L'établissement entier"],
        ],
      },
      note:
        "En dessous de 50 invités, la privatisation est partielle : d'autres clients peuvent être reçus dans les autres espaces. Pour un lieu rien qu'à vous, demandez la privatisation totale.",
    },

    timeline: {
      title: "Le déroulé type d'un mariage à L'Entracte",
      steps: [
        {
          label: "Le vin d'honneur.",
          body:
            "Après la cérémonie à la mairie ou à l'église, vos invités sont accueillis sur la terrasse autour d'une flûte de champagne (ou d'un cocktail sans alcool) et de 5 pièces cocktail par personne préparées par le chef.",
        },
        {
          label: "Le repas.",
          body:
            "Déjeuner ou dîner assis, en 3 ou 4 services, ou formule buffet : vous choisissez le format, nous construisons le menu avec vous.",
        },
        {
          label: "Le dessert.",
          body:
            "Le chef réalise son Saint-Honoré « comme une pièce montée », ou l'un des desserts de la carte. Vous pouvez aussi apporter votre propre gâteau de mariage.",
        },
        {
          label: "La fin de la réception.",
          body:
            "La prestation comprend 3 heures ; vous pouvez prolonger la fête jusqu'à minuit (400 € par heure supplémentaire).",
        },
      ],
      note:
        "Déjeuner ou dîner ? Le déjeuner de mariage (à partir de 12h30) est une formule de plus en plus choisie : lumière naturelle sur la terrasse, invités de tous âges et journée plus détendue.",
      alt: "Pièces cocktail du vin d'honneur préparées par le chef de L'Entracte",
    },

    menu: {
      title: "Le menu de mariage",
      intro:
        "Notre cuisine, traditionnelle et créative, est exclusivement élaborée à partir de produits frais et entièrement faite maison.",
      tableTitle: "Le menu de mariage complet comprend :",
      tableCaption: "Composition du menu de mariage complet",
      tableRows: [
        ["Apéritif", "Une flûte de champagne et 5 pièces cocktail par personne."],
        ["Repas en 4 temps", "Entrée, plat, fromage et dessert."],
        [
          "Boissons",
          "Les eaux plate et gazeuse, une bouteille de vin rouge ou blanc pour 4 personnes, et le café.",
        ],
      ],
      choiceNote:
        "Pour chaque service, vous choisissez 2 propositions dans notre carte événementielle ; la répartition entre les deux est fixée à l'avance avec vous.",
      groupsTitle: "Quelques propositions de la carte :",
      groups: [
        {
          label: "Entrées",
          body:
            "tartare de Saint-Jacques et guacamole d'avocat, coulis de fruits rouges ; saumon gravlax mariné 24 h au sel de Guérande et au poivre de Sichuan ; trio de ravioles Saint-Jacques, cabillaud et crevette, sauce crustacés ; œuf nuage, crème forestière aux morilles ; foie gras mi-cuit, compotée d'oignons et toast brioché ; ballottine de légumes, sauce gingembre-miel (végétarien).",
        },
        {
          label: "Plats",
          body:
            "filet mignon de veau, gratin de légumes de saison et sauce champignons ; filet de bar juste snacké, tian de légumes et pommes dauphine, sauce à l'aneth ; duo de saumon et cabillaud, sauce beurre blanc ; cuisse de canard confite, sauce bordelaise ; risotto crémeux à l'encre de seiche et gambas flambées au whisky (version végétarienne aux champignons).",
        },
        { label: "Fromage", body: "assiette de 3 fromages affinés." },
        {
          label: "Desserts",
          body:
            "Saint-Honoré « comme une pièce montée » ; pavlova à l'orange ; tarte Tatin du chef ; macaron poire-caramel ; cœur fondant au chocolat ; crème brûlée à la vanille.",
        },
      ],
      note:
        "Menus adaptés : végétarien, végétalien, vegan, sans gluten, halal ou biologique, sur demande. Menu enfant (plat et dessert) à 40 €.",
      alt: "Saint-Honoré « comme une pièce montée » du chef de L'Entracte",
      ctaMenu: "Voir la carte",
    },

    budget: {
      title: "Quel budget pour un mariage à L'Entracte ?",
      items: [
        "Nos menus de mariage vont de 99 à 125 € par invité, selon la composition choisie. Ils comprennent le repas, le vin, les eaux, le café, le service et la privatisation de l'espace adapté à votre nombre d'invités.",
        "Le menu enfant (plat et dessert) est à 40 €.",
        "La prestation couvre 3 heures ; chaque heure supplémentaire est facturée 400 €.",
        "Les boissons au-delà de celles incluses dans le menu sont réglées à la consommation.",
        "Nous ne proposons pas de location de salle seule, et les boissons extérieures ne sont pas acceptées.",
      ],
      note:
        "Modalités : acompte de 50 % à la signature du devis, solde le jour J avant la prestation, par chèque ou virement. Le nombre définitif d'invités, les choix de menus et votre heure d'arrivée sont à confirmer 10 jours avant le mariage.",
    },

    suppliers: {
      title: "Vos prestataires et la décoration",
      items: [
        "Photographe : libre, vous choisissez le vôtre.",
        "Décoration : notre équipe peut prendre en charge la décoration de la salle et de la terrasse (compositions florales, centres de table, arches), sur devis.",
        "Gâteau : réalisé par notre chef ou apporté par vos soins.",
        "Restauration et boissons : assurées exclusivement par L'Entracte.",
        "Ambiance : L'Entracte convient aux mariages centrés sur le repas et le partage. Le lieu ne dispose pas de piste de danse dédiée, et la réception se termine au plus tard à minuit.",
      ],
      alt: "Décoration florale de mariage à L'Entracte, Ville-d'Avray",
      ctaGallery: "Voir la galerie",
    },

    access: {
      title: "Accès : un lieu de mariage près de Versailles et de Paris",
      intro:
        "L'Entracte est situé au 3 rue de Versailles, à Ville-d'Avray (Hauts-de-Seine), entre Saint-Cloud et Versailles, à 15 minutes de Paris. Il est tout proche de Sèvres, Chaville et Marnes-la-Coquette.",
      items: [
        "En train : gare de Sèvres – Ville-d'Avray à 1 km (environ 13 minutes à pied), desservie par la ligne L depuis Paris Saint-Lazare et La Défense, et par la ligne U depuis La Défense.",
        "En voiture : stationnement à proximité immédiate du restaurant.",
        "Pour vos invités qui viennent de loin : environ 30 minutes d'Orly et 45 minutes de Roissy-Charles-de-Gaulle.",
        "Hébergement : L'Entracte ne dispose pas de chambres ; l'hôtel Campanile Paris Ouest – Chaville (128 chambres) se trouve à 1 km.",
      ],
    },

    reviews: {
      title: "Ils se sont mariés à L'Entracte",
      ratings: "5/5 sur Mariages.net, recommandé par 100 % des couples · 5/5 sur 1001 Salles",
      quotes: [
        {
          quote: "Très bonne communication en amont, grande flexibilité. Repas et services impeccables.",
          author: "Pierre, mariage (Mariages.net, septembre 2023)",
        },
        {
          quote:
            "Dès notre premier contact, Michel s'est montré très sympathique. Les plats étaient délicieux et très bien présentés.",
          author: "Sam, mariage (1001 Salles, juillet 2023)",
        },
      ],
      link: "Voir le reportage photo d'un mariage célébré à L'Entracte en juillet 2025",
      linkUrl: "https://www.mariages.net/restaurant-mariage/l-entracte-restaurant-&-reception--e278258",
    },

    faqTitle: "Questions fréquentes sur le mariage à L'Entracte",
    faq: [
      {
        question: "Combien d'invités peut-on accueillir pour un mariage ?",
        answer:
          "De 12 à 100 invités assis, et jusqu'à 130 en cocktail en privatisation totale. Le Grand Salon reçoit de 30 à 65 invités assis et la terrasse jusqu'à 90.",
      },
      {
        question: "Quel est le prix d'un mariage à L'Entracte ?",
        answer:
          "Les menus de mariage vont de 99 à 125 € par invité, repas, vin, eaux, café et service compris. Le menu enfant est à 40 €.",
      },
      {
        question: "Que comprend le menu de mariage ?",
        answer:
          "Le menu complet comprend un apéritif (flûte de champagne et 5 pièces cocktail par personne), un repas en 4 temps (entrée, plat, fromage, dessert), les eaux, une bouteille de vin pour 4 personnes et le café.",
      },
      {
        question: "Peut-on louer la salle sans le repas ?",
        answer:
          "Non. La restauration est assurée exclusivement par notre cuisine, et la location de salle seule n'est pas proposée.",
      },
      {
        question: "Jusqu'à quelle heure peut durer la réception ?",
        answer:
          "La prestation comprend 3 heures, au déjeuner (12h30-15h30) ou au dîner (19h30-22h30). Vous pouvez la prolonger jusqu'à minuit, à 400 € par heure supplémentaire.",
      },
      {
        question: "Peut-on danser ?",
        answer:
          "L'Entracte est pensé pour des mariages autour de la table : le lieu ne dispose pas de piste de danse dédiée.",
      },
      {
        question: "Peut-on apporter notre gâteau de mariage ?",
        answer:
          "Oui. Notre chef peut aussi réaliser votre dessert de mariage, comme son Saint-Honoré « comme une pièce montée ».",
      },
      {
        question: "Peut-on apporter nos boissons ?",
        answer:
          "Non. Le vin, les eaux et le café sont inclus dans le menu, et les boissons supplémentaires sont réglées à la consommation.",
      },
      {
        question: "Le photographe est-il imposé ?",
        answer: "Non, vous êtes libres de choisir votre photographe.",
      },
      {
        question: "Proposez-vous des menus végétariens ou adaptés ?",
        answer:
          "Oui : menus végétariens, végétaliens, vegan, sans gluten, halal ou biologiques sur demande.",
      },
      {
        question: "Que se passe-t-il s'il pleut ?",
        answer:
          "Le vin d'honneur et le repas se tiennent dans nos salons, sans changer le déroulé de votre journée.",
      },
      {
        question: "Le lieu est-il accessible aux personnes à mobilité réduite ?",
        answer: "Oui, nos espaces sont accessibles aux personnes à mobilité réduite.",
      },
      {
        question: "Où loger nos invités ?",
        answer:
          "L'Entracte ne propose pas de chambres. L'hôtel Campanile Paris Ouest – Chaville, à 1 km, compte 128 chambres.",
      },
      {
        question: "Comment réserver notre date ?",
        answer:
          "Après validation du devis, un acompte de 50 % confirme la réservation ; le solde est réglé le jour J avant la prestation. Le nombre définitif d'invités et les menus sont à confirmer 10 jours avant. Les samedis de la belle saison partent vite : contactez-nous tôt.",
      },
    ],

    devis: {
      title: "Parlons de votre mariage",
      body:
        "Indiquez-nous votre date, le nombre d'invités et vos envies : nous revenons généralement vers vous sous 24 heures avec une proposition sur-mesure, et nous vous proposons une visite du lieu.",
      contact: "Téléphone : 01 82 00 18 67 · E-mail : restaurantlentractevda@gmail.com",
    },

    related: {
      title: "Autres événements",
      links: [
        { to: "/evenements", label: "Toutes nos réceptions privées" },
        { to: "/mariage-halal-ville-davray", label: "Mariage halal" },
        { to: "/mariage-juif-ville-davray", label: "Mariage juif" },
        { to: "/bapteme", label: "Baptême et communion" },
        { to: "/anniversaire", label: "Anniversaire et fête de famille" },
      ],
    },

    eventType: "Mariage",
  },

  en: {
    meta: {
      title: "Weddings in Ville-d'Avray, near Versailles",
      description:
        "Your wedding in a private townhouse in Ville-d'Avray (92): a 240 m² terrace overlooking a park, 12 to 100 seated guests, wedding menus from €99 to €125 per guest.",
      keywords:
        "wedding Ville d'Avray, wedding venue near Versailles, wedding reception 92, wedding venue Hauts-de-Seine, wedding menu",
    },
    breadcrumb: [
      { label: "Home", to: "/" },
      { label: "Events", to: "/evenements" },
      { label: "Wedding" },
    ],
    overline: "Wedding · Ville-d'Avray (92)",
    h1: "Your wedding in a private townhouse in Ville-d'Avray",
    intro:
      "A 19th-century private townhouse, a 240 m² terrace overlooking a wooded park, two carefully decorated lounges and cuisine made entirely in-house: L'Entracte hosts your wedding for 12 to 100 seated guests, between Saint-Cloud and Versailles, 15 minutes from Paris.",
    ctaDevis: "Request a wedding quote",
    ctaCall: "Call +33 1 82 00 18 67",
    heroAlt: "Wedding on the terrace of L'Entracte, a private townhouse in Ville-d'Avray",

    brief: {
      title: "Your wedding at L'Entracte at a glance",
      caption: "Key information for a wedding at L'Entracte",
      rows: [
        ["Venue", "Private townhouse, 3 rue de Versailles, 92410 Ville-d'Avray"],
        ["Capacity", "12 to 100 seated guests, up to 130 for a cocktail reception"],
        ["Spaces", "Grand Salon, Petit Salon and a 240 m² terrace overlooking the park"],
        ["Formats", "Champagne reception, cocktail, seated lunch or dinner, buffet"],
        ["Wedding menu", "From €99 to €125 per guest, wine, water and coffee included"],
        ["Children's menu", "€40 (main course and dessert)"],
        [
          "Timings",
          "Lunch 12.30-3.30pm or dinner 7.30-10.30pm, extendable until midnight (€400 per additional hour)",
        ],
        [
          "Getting here",
          "Sèvres – Ville-d'Avray station (lines L and U) 1 km away · parking in the immediate vicinity",
        ],
        ["Accessibility", "Spaces accessible to guests with reduced mobility"],
        ["Room-only hire", "No, catering is provided by our own kitchen"],
      ],
    },

    human: {
      title: "A wedding on a human scale",
      p1: "Between the large reception estates of western Paris and municipal function rooms, L'Entracte offers another path: an intimate, elegant wedding in a house steeped in history. You get a private venue, a genuine restaurant kitchen rather than a caterer, and a team that knows every corner of the house.",
      p2: "Michel and his team support you from the first visit to the last coffee.",
      alt: "Grand Salon at L'Entracte set for a wedding meal",
    },

    why: {
      title: "Why choose L'Entracte for your wedding?",
      items: [
        "An exceptional setting on a human scale: a private townhouse and a wooded park, with none of the \"wedding factory\" feel.",
        "100% house-made cuisine: every dish is prepared on site by our chef, from fresh seasonal produce.",
        "A built-in plan B: if the weather turns, the cocktail and the meal are held in our lounges.",
        "Everything included: venue hire, the chef's menu, wine, water, coffee, service and set-up, with a single point of contact.",
        "Easy for your guests to reach: by train from Paris Saint-Lazare and La Défense, or by car 15 minutes from Paris.",
      ],
    },

    spaces: {
      title: "The spaces for your wedding",
      items: [
        {
          name: "The 240 m² terrace",
          body:
            "Up to 90 seated guests and 120 for a cocktail reception. Nestled in greenery and overlooking a wooded park, it hosts the champagne reception and, in fine weather, the meal outdoors.",
          alt: "L'Entracte's 240 m² terrace set for a wedding champagne reception",
        },
        {
          name: "The Grand Salon",
          body:
            "30 to 65 seated guests, up to 75 for a cocktail reception. Bright and carefully decorated, it hosts an elegant seated meal all year round.",
          alt: "Grand Salon at L'Entracte set for a wedding meal",
        },
        {
          name: "The Petit Salon",
          body: "10 to 30 guests, for a very small wedding or a family meal the day after.",
          alt: "Petit Salon at L'Entracte for a small wedding",
        },
        {
          name: "Full venue hire",
          body: "50 to 100 seated guests, up to 130 for a cocktail reception: the whole house is yours.",
          alt: "L'Entracte private townhouse fully hired for a wedding",
        },
      ],
      table: {
        caption: "Private space allocated according to the number of adult guests",
        head: ["Number of adult guests", "Space hired"],
        rows: [
          ["12 to 29", "The Petit Salon"],
          ["30 to 49", "The Grand Salon"],
          ["50 and more (with welcome cocktail)", "The entire venue"],
        ],
      },
      note:
        "Below 50 guests, hire is partial: other guests may be welcomed in the remaining spaces. For a venue entirely to yourself, ask for full hire.",
    },

    timeline: {
      title: "A typical wedding day at L'Entracte",
      steps: [
        {
          label: "The champagne reception.",
          body:
            "After the ceremony at the town hall or church, your guests are welcomed on the terrace with a glass of champagne (or a non-alcoholic cocktail) and 5 canapés per person prepared by the chef.",
        },
        {
          label: "The meal.",
          body:
            "Seated lunch or dinner, in 3 or 4 courses, or a buffet: you choose the format and we build the menu with you.",
        },
        {
          label: "The dessert.",
          body:
            "The chef prepares his Saint-Honoré \"like a wedding cake\", or one of the desserts from the menu. You may also bring your own wedding cake.",
        },
        {
          label: "The end of the reception.",
          body:
            "The package includes 3 hours; you can extend the celebration until midnight (€400 per additional hour).",
        },
      ],
      note:
        "Lunch or dinner? The wedding lunch (from 12.30pm) is an increasingly popular option: natural light on the terrace, guests of all ages and a more relaxed day.",
      alt: "Canapés for the wedding champagne reception prepared by the chef of L'Entracte",
    },

    menu: {
      title: "The wedding menu",
      intro:
        "Our traditional and creative cuisine is made exclusively from fresh produce and entirely in-house.",
      tableTitle: "The full wedding menu includes:",
      tableCaption: "Composition of the full wedding menu",
      tableRows: [
        ["Aperitif", "A glass of champagne and 5 canapés per person."],
        ["4-course meal", "Starter, main course, cheese and dessert."],
        ["Drinks", "Still and sparkling water, one bottle of red or white wine for 4 people, and coffee."],
      ],
      choiceNote:
        "For each course you choose 2 options from our events menu; the split between the two is agreed with you in advance.",
      groupsTitle: "A few suggestions from the menu:",
      groups: [
        {
          label: "Starters",
          body:
            "scallop tartare with avocado guacamole and red berry coulis; gravlax salmon marinated for 24 hours in Guérande salt and Sichuan pepper; trio of ravioli with scallop, cod and prawn, shellfish sauce; cloud egg with woodland morel cream; semi-cooked foie gras, onion compote and brioche toast; vegetable ballotine, ginger and honey sauce (vegetarian).",
        },
        {
          label: "Main courses",
          body:
            "veal fillet mignon, seasonal vegetable gratin and mushroom sauce; seared sea bass fillet, vegetable tian and pommes dauphine, dill sauce; duo of salmon and cod, beurre blanc sauce; confit duck leg, bordelaise sauce; creamy squid-ink risotto with prawns flambéed in whisky (vegetarian version with mushrooms).",
        },
        { label: "Cheese", body: "plate of 3 matured cheeses." },
        {
          label: "Desserts",
          body:
            "Saint-Honoré \"like a wedding cake\"; orange pavlova; the chef's tarte Tatin; pear-caramel macaron; molten chocolate cake; vanilla crème brûlée.",
        },
      ],
      note:
        "Special diets: vegetarian, plant-based, vegan, gluten-free, halal or organic menus on request. Children's menu (main course and dessert) at €40.",
      alt: "Saint-Honoré \"like a wedding cake\" by the chef of L'Entracte",
      ctaMenu: "See the menu",
    },

    budget: {
      title: "What budget for a wedding at L'Entracte?",
      items: [
        "Our wedding menus range from €99 to €125 per guest, depending on the composition chosen. They include the meal, wine, water, coffee, service and hire of the space matching your number of guests.",
        "The children's menu (main course and dessert) is €40.",
        "The package covers 3 hours; each additional hour is charged at €400.",
        "Drinks beyond those included in the menu are settled by consumption.",
        "We do not offer room-only hire, and outside drinks are not permitted.",
      ],
      note:
        "Terms: 50% deposit on signature of the quote, balance on the day before the service, by cheque or bank transfer. The final number of guests, the menu choices and your arrival time are to be confirmed 10 days before the wedding.",
    },

    suppliers: {
      title: "Your suppliers and the decoration",
      items: [
        "Photographer: free choice, you pick your own.",
        "Decoration: our team can take care of decorating the room and the terrace (floral arrangements, table centrepieces, arches), quoted separately.",
        "Cake: made by our chef or brought by you.",
        "Catering and drinks: provided exclusively by L'Entracte.",
        "Atmosphere: L'Entracte suits weddings centred on the meal and sharing. The venue has no dedicated dance floor, and the reception ends by midnight at the latest.",
      ],
      alt: "Wedding floral decoration at L'Entracte, Ville-d'Avray",
      ctaGallery: "See the gallery",
    },

    access: {
      title: "Getting here: a wedding venue near Versailles and Paris",
      intro:
        "L'Entracte is located at 3 rue de Versailles in Ville-d'Avray (Hauts-de-Seine), between Saint-Cloud and Versailles, 15 minutes from Paris. It is very close to Sèvres, Chaville and Marnes-la-Coquette.",
      items: [
        "By train: Sèvres – Ville-d'Avray station 1 km away (about a 13-minute walk), served by line L from Paris Saint-Lazare and La Défense, and by line U from La Défense.",
        "By car: parking in the immediate vicinity of the restaurant.",
        "For guests travelling from afar: about 30 minutes from Orly and 45 minutes from Roissy-Charles-de-Gaulle.",
        "Accommodation: L'Entracte does not have guest rooms; the Campanile Paris Ouest – Chaville hotel (128 rooms) is 1 km away.",
      ],
    },

    reviews: {
      title: "They got married at L'Entracte",
      ratings: "5/5 on Mariages.net, recommended by 100% of couples · 5/5 on 1001 Salles",
      quotes: [
        {
          quote: "Excellent communication beforehand and great flexibility. Impeccable food and service.",
          author: "Pierre, wedding (Mariages.net, September 2023)",
        },
        {
          quote:
            "From our very first contact, Michel was extremely friendly. The dishes were delicious and beautifully presented.",
          author: "Sam, wedding (1001 Salles, July 2023)",
        },
      ],
      link: "See the photo report of a wedding held at L'Entracte in July 2025",
      linkUrl: "https://www.mariages.net/restaurant-mariage/l-entracte-restaurant-&-reception--e278258",
    },

    faqTitle: "Frequently asked questions about weddings at L'Entracte",
    faq: [
      {
        question: "How many guests can you host for a wedding?",
        answer:
          "From 12 to 100 seated guests, and up to 130 for a cocktail reception with full venue hire. The Grand Salon hosts 30 to 65 seated guests and the terrace up to 90.",
      },
      {
        question: "How much does a wedding at L'Entracte cost?",
        answer:
          "Wedding menus range from €99 to €125 per guest, including the meal, wine, water, coffee and service. The children's menu is €40.",
      },
      {
        question: "What does the wedding menu include?",
        answer:
          "The full menu includes an aperitif (a glass of champagne and 5 canapés per person), a 4-course meal (starter, main course, cheese, dessert), water, one bottle of wine for 4 people and coffee.",
      },
      {
        question: "Can we hire the room without the meal?",
        answer:
          "No. Catering is provided exclusively by our own kitchen, and room-only hire is not offered.",
      },
      {
        question: "How late can the reception run?",
        answer:
          "The package includes 3 hours, at lunch (12.30-3.30pm) or dinner (7.30-10.30pm). You can extend it until midnight, at €400 per additional hour.",
      },
      {
        question: "Can we dance?",
        answer:
          "L'Entracte is designed for weddings around the table: the venue has no dedicated dance floor.",
      },
      {
        question: "Can we bring our own wedding cake?",
        answer:
          "Yes. Our chef can also create your wedding dessert, such as his Saint-Honoré \"like a wedding cake\".",
      },
      {
        question: "Can we bring our own drinks?",
        answer:
          "No. Wine, water and coffee are included in the menu, and additional drinks are settled by consumption.",
      },
      {
        question: "Is the photographer imposed?",
        answer: "No, you are free to choose your own photographer.",
      },
      {
        question: "Do you offer vegetarian or special diet menus?",
        answer:
          "Yes: vegetarian, plant-based, vegan, gluten-free, halal or organic menus on request.",
      },
      {
        question: "What happens if it rains?",
        answer:
          "The champagne reception and the meal are held in our lounges, without changing the flow of your day.",
      },
      {
        question: "Is the venue accessible to guests with reduced mobility?",
        answer: "Yes, our spaces are accessible to guests with reduced mobility.",
      },
      {
        question: "Where can our guests stay?",
        answer:
          "L'Entracte does not offer guest rooms. The Campanile Paris Ouest – Chaville hotel, 1 km away, has 128 rooms.",
      },
      {
        question: "How do we secure our date?",
        answer:
          "Once the quote is approved, a 50% deposit confirms the booking; the balance is settled on the day before the service. The final number of guests and the menus are to be confirmed 10 days before. Saturdays in the high season go quickly: contact us early.",
      },
    ],

    devis: {
      title: "Let's talk about your wedding",
      body:
        "Tell us your date, the number of guests and what you have in mind: we usually get back to you within 24 hours with a bespoke proposal, and we invite you to visit the venue.",
      contact: "Phone: +33 1 82 00 18 67 · Email: restaurantlentractevda@gmail.com",
    },

    related: {
      title: "Other events",
      links: [
        { to: "/evenements", label: "All our private receptions" },
        { to: "/mariage-halal-ville-davray", label: "Halal wedding" },
        { to: "/mariage-juif-ville-davray", label: "Jewish wedding" },
        { to: "/bapteme", label: "Christenings and communions" },
        { to: "/anniversaire", label: "Birthdays and family celebrations" },
      ],
    },

    eventType: "Wedding",
  },
};
